import { ArrowRightLeft, PackagePlus, XCircle } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import type { MenuItem } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { evidenceCaseRequestsApi } from '../evidenceApi';
import { DESTINATION, DESTINATION_LABEL, REQUEST_KIND, type EvidenceRequestDetail } from '../evidenceTypes';
import { personnelLabel, usePersonName, usePersonnelNames } from '../../../human-resources/personnel/useStaff';
import { EvidenceIntakeModal } from './EvidenceIntakeModal';
import { EvidenceRequestDeclineModal } from './EvidenceRequestDeclineModal';
import { EvidenceTransferModal } from './EvidenceTransferModal';

/**
 * A case's EVIDENCE REQUESTS — the Evidence tab's first folder (product call, 09 Oct 2026: *all of
 * these requests will appear in the case profile, evidence, a new tab above received "Requests"*).
 *
 * <b>ONLY WHAT WAITS</b>, as Received is: a request leaves the folder once the team acts on it — the
 * requester's own register keeps its whole history. Oldest first, because the one waiting longest
 * is the one to act on.
 *
 * <b>Acting on one is the REAL form</b>, pre-filled: Receive for a receiving request, Transfer for a
 * transfer request — each promoted beside the kebab, on the key and the case flag the act itself
 * needs. Completing it fulfils the request. <b>Decline</b> sits in the kebab, on the same key and
 * flag, and asks for a reason.
 */
export function EvidenceCaseRequestsPanel({
  caseId,
  reloadKey,
  uploadReason,
  moveReason,
  onChanged,
}: {
  caseId: string;
  reloadKey: number;
  /** Why this reader cannot RECEIVE here (the case's Upload flag, or a deleted case), or null. */
  uploadReason: string | null;
  /** Why this reader cannot TRANSFER here (the case's Edit flag, or a deleted case), or null. */
  moveReason: string | null;
  /** A request was acted on — every folder's contents and the page's counts move. */
  onChanged: () => void;
}) {
  const toast = useToast();
  const [receiving, setReceiving] = useState<EvidenceRequestDetail | null>(null);
  const [transferring, setTransferring] = useState<EvidenceRequestDetail | null>(null);
  const [declining, setDeclining] = useState<EvidenceRequestDetail | null>(null);

  const fetchPage = useCallback(
    (query: DataTableQuery, signal: AbortSignal) =>
      evidenceCaseRequestsApi.list(caseId, { ...query, sort: query.sort ?? undefined }, signal),
    // reloadKey re-creates the callback, which is what makes the table refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [caseId, reloadKey],
  );

  const state = useDataTable<EvidenceRequestDetail>({ fetchPage, pageSize: 25 });

  // The people on the page on screen, resolved in ONE batch — the PII seam's rule.
  const personnelIds = useMemo(() => state.rows.map((r) => r.requesterPersonnelId), [state.rows]);
  const { names, canRead } = usePersonnelNames(personnelIds);

  const columns = useMemo<DataColumn<EvidenceRequestDetail>[]>(
    () => [
      {
        key: 'requestNumber',
        header: 'Request No.',
        width: 150,
        sortable: true,
        render: (row) => <TextCell value={row.requestNumber} mono />,
      },
      {
        key: 'kind',
        header: 'Type',
        width: 120,
        sortable: true,
        summary: true,
        render: (row) => <TextCell value={row.kindLabel} />,
      },
      {
        key: 'requestor',
        header: 'Requestor',
        width: 200,
        summary: true,
        render: (row) => <TextCell value={personnelLabel(row.requesterPersonnelId, names, canRead)} />,
      },
      {
        // A receiving request goes nowhere — its em dash says the field does not apply, not that it is unknown.
        key: 'destination',
        header: 'Transfer to',
        width: 130,
        render: (row) => <TextCell value={row.destination != null ? DESTINATION_LABEL[row.destination] : null} />,
      },
      {
        key: 'items',
        header: 'Items',
        width: 90,
        summary: true,
        render: (row) => <TextCell value={String(row.lines.length)} mono numeric />,
      },
    ],
    [names, canRead],
  );

  /*
    THE ACT the request asks for, promoted beside the kebab — one per row, by its kind. Its KEY hides it;
    the case's flag is a STATE of this reader on this case, so it disables with the reason.
  */
  const actFor = (row: EvidenceRequestDetail): MenuItem =>
    row.kind === REQUEST_KIND.receiving
      ? {
          key: 'receive',
          label: 'Receive',
          icon: <PackagePlus className="h-4 w-4" aria-hidden="true" />,
          permission: 'evidence-items.create',
          disabledReason: uploadReason,
          onClick: () => setReceiving(row),
        }
      : {
          key: 'transfer',
          label: 'Transfer',
          icon: <ArrowRightLeft className="h-4 w-4" aria-hidden="true" />,
          permission: 'evidence-items.move',
          disabledReason: moveReason,
          onClick: () => setTransferring(row),
        };

  // DECLINE carries the key and the flag FULFILLING it would — the server's two routes, one per kind.
  const declineFor = (row: EvidenceRequestDetail): MenuItem => ({
    key: 'decline',
    label: 'Decline',
    icon: <XCircle className="h-4 w-4" aria-hidden="true" />,
    permission: row.kind === REQUEST_KIND.receiving ? 'evidence-items.create' : 'evidence-items.move',
    disabledReason: row.kind === REQUEST_KIND.receiving ? uploadReason : moveReason,
    onClick: () => setDeclining(row),
  });

  // The page bumps `reloadKey` on `onChanged`, which re-creates `fetchPage` — this table refetches with it.
  function acted(message: string) {
    toast.success(message);
    onChanged();
  }

  return (
    <>
      <DataTablePanel
        id="evidence-case-requests"
        state={state}
        columns={columns}
        rowKey={(row) => row.id}
        searchPlaceholder="Search request no."
        fill
        rowPreview={{
          title: 'Evidence request',
          size: '3xl',
          details: (row) => <RequestPreview request={row} />,
        }}
        rowQuickActions={(row) => [actFor(row)]}
        rowActions={(row) => [declineFor(row)]}
        otherInfo={(row) => row}
        emptyMessage="No request is waiting. Requests an employee raises from Self Service appear here until the case's team acts on them."
        emptySearchMessage="No waiting request matches that search."
      />

      {receiving ? (
        <EvidenceIntakeModal
          caseId={caseId}
          fromRequest={receiving}
          onClose={() => setReceiving(null)}
          onSaved={(saved) => {
            const number = receiving.requestNumber;
            setReceiving(null);
            acted(`${number} was received as ${saved.receivingNumber}.`);
          }}
        />
      ) : null}

      {transferring ? (
        <EvidenceTransferModal
          caseId={caseId}
          fromRequest={transferring}
          onClose={() => setTransferring(null)}
          onSaved={(result, destination) => {
            const number = transferring.requestNumber;
            setTransferring(null);
            acted(
              `${number}: ${result.itemCount} item${result.itemCount === 1 ? '' : 's'} sent for approval — ${DESTINATION_LABEL[destination].toLowerCase()}. Nothing moves until it is approved.`,
            );
          }}
        />
      ) : null}

      {declining ? (
        <EvidenceRequestDeclineModal
          request={declining}
          onClose={() => setDeclining(null)}
          onDeclined={() => {
            const number = declining.requestNumber;
            setDeclining(null);
            acted(`${number} was declined.`);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The request as the requester gave it — its destination's details, then its items. A component of
 * its own because the JUDGE resolves through a hook: the name is read from the identity store, never
 * carried on the row.
 */
function RequestPreview({ request }: { request: EvidenceRequestDetail }) {
  const judge = usePersonName(request.judgePersonId);
  const acquirers = useMemo(() => request.lines.map((l) => l.acquiredByPersonnelId), [request.lines]);
  const { names, canRead } = usePersonnelNames(acquirers);
  const isTransfer = request.kind === REQUEST_KIND.transfer;

  return (
    <div className="flex flex-col gap-4">
      {isTransfer ? (
        <PreviewTable
          variant="table-display"
          rows={
            request.destination === DESTINATION.court
              ? [
                  { label: 'Court Branch', value: request.courtBranch },
                  { label: 'Court Order No.', value: request.courtOrderNumber, mono: true },
                  { label: 'Court order file', value: request.courtOrderFile },
                  { label: 'Judge', value: judge },
                ]
              : [
                  { label: 'Disposal Type', value: request.disposalTypeName },
                  { label: 'Reason', value: request.reason },
                ]
          }
        />
      ) : null}

      <div className="flex flex-col gap-1.5">
        <p className="text-sm font-medium text-slate-700">Items ({request.lines.length})</p>
        <PreviewTable
          variant="table-display"
          rows={request.lines.map((line) => ({
            label: isTransfer ? (line.inventoryNumber ?? `Item ${line.lineNumber}`) : `Item ${line.lineNumber}`,
            value: isTransfer ? (
              `${line.typeName ?? 'Item'} — ${line.description ?? ''}`
            ) : (
              <>
                {`${line.typeName ?? 'Item'} — ${line.description ?? ''}`}
                {' · acquired '}
                {/* MONO, like every date. */}
                <time dateTime={toIso(line.acquiredAt ?? null)} className="font-mono">
                  {formatDateTime(line.acquiredAt ?? null)}
                </time>
                {` by ${personnelLabel(line.acquiredByPersonnelId, names, canRead) ?? '…'}`}
                {line.attachments.length > 0 ? ` · ${line.attachments.map((a) => a.fileName).join(', ')}` : null}
              </>
            ),
          }))}
        />
      </div>
    </div>
  );
}
